import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));

    // Apply the correct, server-computed checkout URL to every variant's Buy Now
    // button on load. The variant-change handlers only fire on `change`, so without
    // this the first/default variant keeps whatever href was rendered server-side.
    this.#applyCheckoutUrls();
  }

  /**
   * Apply each variant's server-computed checkout URL (rendered on
   * `.buy-container[data-checkout-url]` in blocks/buy-buttons-variant.liquid) to the
   * Buy Now button(s) inside that container. Computing the URL in the Liquid loop
   * avoids the `content_for` first-variant mis-binding and preserves the
   * `main_variant` mapping + discount that a bare `/cart/{id}` link would drop.
   * @param {ParentNode} [root] - Optional scope; defaults to this product's section.
   */
  #applyCheckoutUrls(root) {
    const productId = this.dataset.productId;
    const scope =
      root ||
      (productId && document.querySelector(`.product__section.product-${productId}`)) ||
      this.closest?.('.product__section') ||
      document;

    scope.querySelectorAll?.('.buy-container[data-checkout-url]').forEach((container) => {
      const url = container.dataset.checkoutUrl;
      if (!url) return;
      container.querySelectorAll('[data-buy-now-button]').forEach((btn) => {
        btn.setAttribute('href', url);
      });
    });
  }

  
  /**
   * Runs your custom UI updates whenever a variant changes.
   * Keeps it scoped to this picker + current product.
   */runCustomVariantUpdates(selectedOption) {
  // ---- Resolve variant id safely ----
  const variantId =
    selectedOption?.dataset?.variantId ||
    selectedOption?.getAttribute?.('data-variant-id');

  if (!variantId) return;

  const productId = this.dataset.productId;

  // ---- Find a stable root to query inside (prevents updating the wrong copy after morph) ----
  const productRoot =
    (productId && document.querySelector(`.product__section.product-${productId}`)) ||
    this.closest?.('.product__section') ||
    document;

  // ---- helper: run the update pass (we will call it multiple times) ----
  const apply = () => {
    // Re-resolve selectedOption in case DOM was morphed
    // Prefer checked input inside THIS picker
    const liveSelected =
      this.querySelector?.('fieldset input[data-variant-variant-input]:checked') ||
      selectedOption;

    const liveVariantId =
      liveSelected?.dataset?.variantId ||
      liveSelected?.getAttribute?.('data-variant-id') ||
      variantId;

    // ---------- read dataset values ----------
    const availability = liveSelected?.dataset?.available;
    const checkoutUrl = liveSelected?.dataset?.checkoutUrl;
    const variantMediaId = liveSelected?.dataset?.mediaId;

    // ---------- 1) SWITCH VARIANT PRICE BLOCKS (scope to productRoot if possible) ----------
    productRoot.querySelectorAll?.('.price-container').forEach((block) => {
      block.setAttribute('hidden', 'true');
    });
    productRoot
      .querySelectorAll?.(`.price-container.variant-${CSS.escape(String(liveVariantId))}`)
      .forEach((block) => {
        block.removeAttribute('hidden');
      });

    // ---------- 2) SWITCH VARIANT ORDER/QUANTITY FORMS ----------
    productRoot.querySelectorAll?.('.buy-container[data-buy-variant-id]').forEach((form) => {
      form.setAttribute('hidden', 'true');
    });
    productRoot
      .querySelectorAll?.(`.buy-container[data-buy-variant-id="${CSS.escape(String(liveVariantId))}"]`)
      .forEach((form) => {
        form.removeAttribute('hidden');
      });

    // Re-affirm the correct Buy Now hrefs after show/hide (and after any morph that
    // replaced the containers), so the visible variant always points to the right URL.
    this.#applyCheckoutUrls(productRoot);

    // ---------- 3) Update sticky text -> .main_abar ----------
    // Try closest label first; fallback to label wrapping the checked input
    let label =
      liveSelected?.closest?.('label') ||
      liveSelected?.closest?.('.variant-option__button-label') ||
      null;

    if (!label && liveSelected?.id) {
      label = this.querySelector?.(`label[for="${CSS.escape(liveSelected.id)}"]`) || null;
    }

    if (label) {
      const sticky = label.querySelector('.sticky-text');
      // main_abar might live outside productRoot, so try productRoot then document
      const mainAbar =
        productRoot.querySelector?.('.main_abar') || document.querySelector('.main_abar');

      if (sticky && mainAbar) {
        mainAbar.innerHTML = sticky.innerHTML;
      }
    }

    // ---------- 4) Update abar link ----------
    const abarLink =
      productRoot.querySelector?.('.extra_abar a') || document.querySelector('.extra_abar a');

    if (abarLink) {
      if (availability === '1' && checkoutUrl) {
        abarLink.setAttribute('href', checkoutUrl);
        abarLink.setAttribute('aria-disabled', 'false');
      } else {
        // safer than href="" (which can navigate to same page)
        abarLink.removeAttribute('href');
        abarLink.setAttribute('aria-disabled', 'true');
      }
    }

    // ---------- 5) Variant gallery switching ----------
    if (productId) {
      const wrappers = document.querySelectorAll(
        `.product__section.product-${CSS.escape(String(productId))}[data-variant-id]`
      );

      wrappers.forEach((wrapper) => {
        const matches = String(wrapper.dataset.variantId) === String(liveVariantId);
        if (matches) {
          const wasHidden = wrapper.hasAttribute('hidden');
          wrapper.removeAttribute('hidden');

          if (wasHidden) {
            const galleryRoot = wrapper.querySelector('.js-product-gallery[data-product-gallery]');
            if (galleryRoot) {
              galleryRoot.dispatchEvent(new CustomEvent('gallery:show', { bubbles: true }));
            }
          }
        } else {
          wrapper.setAttribute('hidden', 'true');
        }
      });
    }

    // ---------- 6) Flickity: select slide by media id ----------
    if (
      productId &&
      variantMediaId &&
      typeof window.selectFlickitySlideByMediaId === 'function'
    ) {
      window.selectFlickitySlideByMediaId(productId, variantMediaId);
    }
  };

  // ---- Run immediately (instant UX) ----
  apply();

  // ---- Run again next frame (survives some morph timing) ----
  requestAnimationFrame(apply);

  // ---- Run once more slightly later (survives fetch+morph replacing DOM) ----
  // Keep small; too large feels laggy.
  window.setTimeout(apply, 60);
}




  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {

    if (!(event.target instanceof HTMLElement)) return;

    // --------------------------
    // SCROLL LOCK (keep page where it is)
    // --------------------------
    const startScrollX = window.scrollX;
    const startScrollY = window.scrollY;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    // Blur the control so mobile browsers don't auto-scroll to keep it focused
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) {
      event.target.blur();
    }

    this.dispatchEvent(
      new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' })
    );

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);
    const variantId = selectedOption.dataset.variantId || null;

    // --------------------------
    // VARIANT-DEPENDENT UPDATES
    // --------------------------
    if (variantId) {
      
      console.log(
        '%cDispatching variant:changed',
        'color: blue; font-weight: bold;',
        variantId
      );
// --- Your custom-only mode ---
const isCustomOnly = this.dataset.customOnly === 'true';

// Run your custom updates for your radios (safe even if not custom)
if (selectedOption instanceof HTMLInputElement && selectedOption.matches('[data-variant-variant-input]')) {
  this.runCustomVariantUpdates?.(selectedOption);
  if (isCustomOnly) return; // ✅ stop here: no Horizon price updates, no fetch/morph, etc.
}


      // --------------------------
      // UPDATE VARIANT LABEL
      // --------------------------
      const variantLabelText =
        selectedOption.dataset.optionLabel ||
        selectedOption.textContent?.trim() ||
        selectedOption.value;

      const variantLabelEls = document.querySelectorAll('[data-variant-label]');
      variantLabelEls.forEach((el) => {
        el.textContent = variantLabelText;
      });

      // --- Parse prices from data-attributes safely ---
      const variantPrice = Number(selectedOption.dataset.variantPrice || 0);
      const comparePriceRaw = selectedOption.dataset.comparePrice;
      const comparePrice = comparePriceRaw ? Number(comparePriceRaw) : null;
      let savings = null;
      if (comparePrice && comparePrice > variantPrice) {
        savings = (comparePrice - variantPrice) / 100;
      }

      const money = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      });

      const saveElems = document.querySelectorAll('.sale-item--amount');
      saveElems.forEach(elem => {
        if (savings !== null) {
          elem.textContent = `Save ${money.format(savings)}`;
        } else {
          // hide saving badge when no compare price
          elem.closest('.price__badge')?.classList.add('hidden');
        }
      });

      const saleText = money.format(variantPrice / 100);
      const compareText =
        comparePrice && comparePrice > variantPrice
          ? money.format(comparePrice / 100)
          : null;

      // 1) Update all hidden variantId inputs inside add-to-cart forms
      const variantInputs = document.querySelectorAll(
        'form[data-type="add-to-cart-form"] input[name="id"][ref="variantId"]'
      );
      variantInputs.forEach((input) => {
        input.value = variantId;
      });

      // 2) Update "Add to cart" inline price text (.show_price_btn)
      const showPriceSpans = document.querySelectorAll(
        'form[data-type="add-to-cart-form"] .show_price_btn'
      );
      showPriceSpans.forEach((span) => {
        let html = ` - ${saleText}`;
        if (compareText) html += ` <del>${compareText}</del>`;
        span.innerHTML = html;
      });

      // 3) Update Buy Now buttons (href + price text)
      const buyButtons = document.querySelectorAll('[data-buy-now-button]');
      if (buyButtons.length) {
        const jsonEl = this.querySelector('script[type="application/json"]');
        if (!jsonEl) {
          console.warn('No variant JSON found inside this picker');
        } else {
          // Keep this if you need variantData later
          try {
            const variantData = JSON.parse(jsonEl.textContent);
            // use variantData if needed
            void variantData;
          } catch (e) {
            console.warn('Error parsing variant JSON', e);
          }
        }

        // Use the selected variant's server-computed checkout URL (preserves the
        // main_variant mapping + discount). Never fall back to a bare /cart/{id},
        // which drops both and sends the buyer to the wrong product/no coupon.
        const activeBuyContainer = document.querySelector(
          `.buy-container[data-buy-variant-id="${CSS.escape(String(variantId))}"][data-checkout-url]`
        );
        const correctCheckoutUrl = activeBuyContainer?.dataset?.checkoutUrl;

        buyButtons.forEach((buyButton) => {
          // Only rewrite the href of buttons inside the active variant's container.
          if (correctCheckoutUrl && activeBuyContainer.contains(buyButton)) {
            buyButton.setAttribute('href', correctCheckoutUrl);
          }

          // Update price text inside Buy Now button
          const priceSpan = buyButton.querySelector('span');
          if (priceSpan) {
            let html = ` - ${saleText}`;
            if (compareText) html += ` <del>${compareText}</del>`;
            priceSpan.innerHTML = html;
          }
        });
      }

      // 4) Update main product price block
      // Sale price
      const saleSpans = document.querySelectorAll('[data-sale-price]');
      saleSpans.forEach((span) => {
        span.textContent = saleText;
      });

      // Compare-at price
      const compareSpans = document.querySelectorAll('[data-regular-price]');
      compareSpans.forEach((span) => {
        const wrapper = span.closest('.compare_sale');
        if (compareText) {
          span.innerHTML = `(<del>${compareText}</del>)`;
          if (wrapper) wrapper.classList.remove('hidden');
        } else {
          // hide compare price if variant has no compare_at_price
          if (wrapper) wrapper.classList.add('hidden');
        }
      });

      // --------------------------
      // 5) Variant media / gallery switching
      // --------------------------
      try {
        const productId = this.dataset.productId;
        if (productId) {
          const wrappers = document.querySelectorAll(
            `.product__section.product-${productId}[data-variant-id]`
          );

          wrappers.forEach((wrapper) => {
            const wrapperVariantId = wrapper.dataset.variantId;
            const matchesVariant = String(wrapperVariantId) === String(variantId);
            const variantHasCustomMedia =
              Number(wrapper.dataset.hasCustomMedia || '0') > 0;

            if (matchesVariant) {
              if (variantId && variantHasCustomMedia) {
                // Variant has custom images – show its custom gallery
                wrapper.removeAttribute('hidden');

                const galleryRoot = wrapper.querySelector(
                  '.js-product-gallery[data-product-gallery]'
                );
                if (galleryRoot) {
                  galleryRoot.dispatchEvent(
                    new CustomEvent('gallery:show', {
                      bubbles: true,
                      detail: { root: galleryRoot },
                    })
                  );
                }
              } else if (variantId) {
                // Variant has no custom media – still show its gallery (fallback/product images)
                wrapper.removeAttribute('hidden');

                const galleryRoot = wrapper.querySelector(
                  '.js-product-gallery[data-product-gallery]'
                );
                if (galleryRoot) {
                  galleryRoot.dispatchEvent(
                    new CustomEvent('gallery:show', {
                      bubbles: true,
                      detail: { root: galleryRoot },
                    })
                  );
                }
              }
            } else {
              // Hide galleries of other variants
              wrapper.setAttribute('hidden', 'hidden');
            }
          });
        }
      } catch (err) {
        console.warn('Error while switching variant media gallery', err);
      }
    }

    // --------------------------
    // URL + HISTORY MANAGEMENT
    // --------------------------
    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct && newUrl) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    // Restore scroll after all the logic above runs
    requestAnimationFrame(() => {
      window.scrollTo(startScrollX, startScrollY);
    });
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              }),
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // --------------------------
    // Prevent visible movement when morphing the picker
    // --------------------------
    const beforeRect = this.getBoundingClientRect();
    morph(this, newVariantPickerSource);
    const afterRect = this.getBoundingClientRect();

    const deltaY = beforeRect.top - afterRect.top;
    if (Math.abs(deltaY) > 1) {
      window.scrollBy(0, deltaY);
    }

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    const beforeRect = main.getBoundingClientRect();
    morph(main, newMain);
    const afterRect = main.getBoundingClientRect();

    const deltaY = beforeRect.top - afterRect.top;
    if (Math.abs(deltaY) > 1) {
      window.scrollBy(0, deltaY);
    }
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
